import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';

import {
  collectIssueTypeSuggestions,
  normalizeIssueType,
  normalizeIssueTypes,
} from '../../utils/responsePartnerUtils';

const IssueTypeTagEditor = ({
  value = [],
  onChange,
  partners = [],
  rules = [],
  selectedPartnerId,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const selectedTags = useMemo(() => normalizeIssueTypes(value), [value]);

  const allSuggestions = useMemo(
    () =>
      collectIssueTypeSuggestions({
        partners,
        rules,
        selectedPartnerId,
      }),
    [partners, rules, selectedPartnerId]
  );

  const filteredSuggestions = useMemo(() => {
    const query = normalizeIssueType(inputValue);
    return allSuggestions.filter(
      (tag) => !selectedTags.includes(tag) && (!query || tag.includes(query))
    );
  }, [allSuggestions, inputValue, selectedTags]);

  const commitTags = (nextTags) => {
    onChange?.(normalizeIssueTypes(nextTags));
  };

  const addTag = (raw) => {
    const tag = normalizeIssueType(raw);
    if (!tag) return;
    if (selectedTags.includes(tag)) return;
    commitTags([...selectedTags, tag]);
    setInputValue('');
    setShowSuggestions(false);
  };

  const removeTag = (tag) => {
    commitTags(selectedTags.filter((t) => t !== tag));
  };

  const handleInputKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addTag(inputValue);
    } else if (event.key === 'Backspace' && !inputValue && selectedTags.length > 0) {
      removeTag(selectedTags[selectedTags.length - 1]);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1.5 min-h-[2rem] p-2 border border-stone-300 rounded-md bg-white">
        {selectedTags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-sky-50 text-sky-800 border border-sky-200 text-xs"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="bg-transparent text-sky-700 hover:text-sky-900 p-0 m-0 leading-none"
              aria-label={`Remove ${tag}`}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          onKeyDown={handleInputKeyDown}
          placeholder={selectedTags.length === 0 ? 'Type and press Enter (e.g. hvac, water)' : 'Add issue type…'}
          className="flex-grow min-w-[8rem] border-0 outline-none text-sm p-0 m-0 bg-transparent"
        />
      </div>

      {showSuggestions && filteredSuggestions.length > 0 && (
        <ul className="border border-stone-200 rounded-md bg-white shadow-sm max-h-32 overflow-y-auto text-xs m-0 p-1 list-none">
          {filteredSuggestions.slice(0, 12).map((tag) => (
            <li key={tag}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addTag(tag)}
                className="w-full text-left px-2 py-1 rounded bg-transparent text-stone-700 hover:bg-stone-100"
              >
                {tag}
              </button>
            </li>
          ))}
        </ul>
      )}

      <span className="text-xs text-stone-500">
        Match issue types such as loss events (water, fire) or partner service lines (hvac, plumbing).
        Leave empty to match any issue type.
      </span>
    </div>
  );
};

export default IssueTypeTagEditor;
